import React from "react";
import { Container, ConnectedAlert, ProgressBar } from "../../../../components";
import { Text, Heading, Button } from "@telia/teddy";
import moment from "moment";
import { connect } from "react-redux";
import { RootState } from "../../../../../../models";
import { isOrderNotFound as isOrderNotFoundSelector } from "../../../../app/state/selectors";
import {
    getHasRejectedTransferOrder,
    getTransferBreakageFee,
    getHasSubmittedTransferOrder,
    getTransferNewLegalOwnerField,
    getTransferExistingIndividualLegalOwnerFullName,
    getSelectedPhoneNumber,
    getTransferDate,
} from "../../../../checkout/state/selectors/accountSelectors";
import { selectors as LOADING_SELECTORS } from "../../../../loading";
import { getFormattedPhoneNumber } from "../../../../rapidform/formValidation";
import InformationTable from "../../../components/molecules/InformationTable/InformationTable";
import TEXT from "../../../text-consts";

interface StateProps {
    breakageFee: number;
    existingLegalOwner: string;
    hasRejectedOrder: boolean;
    hasSubmittedTransferOrder: boolean;
    isFetchingData: boolean;
    isOrderNotFound: boolean;
    organizationName: string;
    selectedPhoneNumber: string;
    transferDate?: string;
}

type SummaryPageToB2BProps = StateProps;

const SummaryPageToB2B: React.FC<SummaryPageToB2BProps> = ({
    // Redux (data)
    isFetchingData,
    isOrderNotFound,
    existingLegalOwner,
    organizationName,
    breakageFee,
    hasRejectedOrder,
    hasSubmittedTransferOrder,
    transferDate,
    selectedPhoneNumber,
}) => {
    const backToFrontPage = () => {
        window.location.assign("https://www.telia.no/");
    };

    const isB2BTransfer = !!organizationName;

    if (isOrderNotFound) {
        return (
            <div className="tlo-landing-page">
                <div className="tlo-landing-page__heading-container">
                    <Heading mb="100" as="h1" variant="title-600">
                        {TEXT.LANDING_PAGE_TO_B2B.HEADING}
                    </Heading>
                </div>
                <Container size="medium">
                    <section className="tlo-landing-page__order-not-found">
                        <Heading mb="100" as="h2" variant="title-200">
                            {TEXT.LANDING_PAGE_TO_B2B.ORDER_NOT_FOUND.HEADING}
                        </Heading>
                        <Text mb="100">{TEXT.LANDING_PAGE_TO_B2B.ORDER_NOT_FOUND.TEXT}</Text>
                        <Text mb="100">{TEXT.LANDING_PAGE_TO_B2B.ORDER_NOT_FOUND.HELP_TEXT}</Text>
                    </section>
                </Container>
            </div>
        );
    }

    const data = [
        { label: "Overføres til:", value: isB2BTransfer ? organizationName : existingLegalOwner },
        { label: "Telefonnummer:", value: getFormattedPhoneNumber(selectedPhoneNumber) },
        {
            label: "Ønsket overføringsdato:",
            value: transferDate ? moment(transferDate).format("DD.MM.YYYY") : "Ingen dato angitt",
        },
    ];

    if (breakageFee) {
        data.push({ label: "Bruddgebyr", value: `${breakageFee},-` });
    }

    return (
        <div className="tlo-landing-page">
            <div className="tlo-landing-page__heading-container">
                <Heading mb="100" as="h1" variant="title-600">
                    {TEXT.LANDING_PAGE_TO_B2B.HEADING}
                </Heading>
            </div>
            <ProgressBar isFetchingData={isFetchingData} backgroundColor="white" />
            <ConnectedAlert />
            {!hasRejectedOrder && hasSubmittedTransferOrder && (
                <Container size="medium">
                    <section>
                        <Heading mb="100" as="h2" variant="title-200">
                            {TEXT.LANDING_PAGE_B2B_APPROVED.APPROVAL_HEADING}
                        </Heading>
                        <Text mb="100">{TEXT.LANDING_PAGE_B2B_APPROVED.RECEIVED_APPROVAL}</Text>
                        <InformationTable data={data} withBorder={true} withHr={false} data-di-mask />
                    </section>
                    <section className="tlo-landing-page__back-to-frontpage">
                        <Button variant="primary" type="submit" onClick={backToFrontPage} disabled={false}>
                            {TEXT.LANDING_PAGE_B2B_REJECTION.BUTTON_TEXT}
                        </Button>
                    </section>
                </Container>
            )}
            {hasRejectedOrder && (
                <Container size="medium">
                    <section>
                        <Heading mb="100" as="h2" variant="title-200">
                            {TEXT.LANDING_PAGE_B2B_REJECTION.REJECTION_HEADING}
                        </Heading>
                        <div
                            className="paragraph"
                            dangerouslySetInnerHTML={{
                                __html: TEXT.LANDING_PAGE_B2B_REJECTION.RECEIVED_REJECTION(
                                    getFormattedPhoneNumber(selectedPhoneNumber),
                                ),
                            }}
                            data-di-mask
                        />
                        <div
                            className="paragraph"
                            dangerouslySetInnerHTML={{ __html: TEXT.LANDING_PAGE_B2B_REJECTION.REJECTION_TEXT }}
                        />
                    </section>
                    <section className="tlo-landing-page__back-to-frontpage">
                        <Button variant="primary" type="submit" onClick={backToFrontPage} disabled={false}>
                            {TEXT.LANDING_PAGE_B2B_REJECTION.BUTTON_TEXT}
                        </Button>
                    </section>
                </Container>
            )}
        </div>
    );
};

const mapStateToProps = (state: RootState): StateProps => ({
    isFetchingData: LOADING_SELECTORS.isLoading(state),
    isOrderNotFound: isOrderNotFoundSelector(state),
    hasRejectedOrder: getHasRejectedTransferOrder(state),
    breakageFee: getTransferBreakageFee(state),
    hasSubmittedTransferOrder: getHasSubmittedTransferOrder(state),
    organizationName: getTransferNewLegalOwnerField(state, "organization", "name"),
    existingLegalOwner: getTransferExistingIndividualLegalOwnerFullName(state) || "",
    selectedPhoneNumber: getSelectedPhoneNumber(state),
    transferDate: getTransferDate(state),
});

export default connect(mapStateToProps)(SummaryPageToB2B);
